import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { Link as RouterLink } from "react-router-dom";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import CustomTextField from "@components/inputs/customTextField";
import RemarkDialog from "@components/dialog/remarkDialog";
import FormPDF from "./formPDF";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { formatDate, formatDateTime } from "@utils/formatter";

import { ACTION, PAYMENT_TERM } from "@utils/constant";

import ViewFormDialog from "./viewFormDialog";


const groupAnswersByQuestion = (data) => {
  const grouped = {};

  data.forEach(({ formAnswerGuid, question, answer, remark, data }) => {
      if (!grouped[question]) {
          grouped[question] = { question, data, answers: [] };
      }
      grouped[question].answers.push({ formAnswerGuid, answer, remark });

      // Ensure `data` is taken from the first occurrence if multiple exist
      if (grouped[question].data === undefined) {
          grouped[question].data = data;
      }
  });

  return Object.values(grouped);
}

export default function ApplicationForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user, isCurrentModuleAdmin } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");


  const [data, setData] = useState(null)

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const [openRemarkDialog, setOpenRemarkDialog] = useState(false)
  const [remarkDialogAction, setRemarkDialogAction] = useState("")

  const [openFormDialog, setOpenFormDialog] = useState(false)

  useEffect(()=>{
    if (!id) {
      onBack()
    }
    if (id) {
      fetchApplicationMutation.mutate(id)
    }
  },[id])

  const fetchApplicationMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getApplication}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      data.formAnswers = groupAnswersByQuestion(data?.formDetails?.formAnswers)
      data.formAnswersData = data.formAnswers.filter(({ data }) => data !== null).sort((a, b) => a.data - b.data);;
      // console.log(data)
      setData(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve application", "error");
    }
  });

  const approveApplicationMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.approveApplication}/${id}`, data)
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getApplication'] })
      openSnackbar("Successfully approved application", "success");
      fetchApplicationMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to approve application", "error");
    }
  })

  const kivApplicationMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.kivApplication}/${id}`, data)
    },
    onSuccess: () => {
      setOpenRemarkDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getApplication'] })
      openSnackbar("Successfully kiv-ed application", "success");
      fetchApplicationMutation.mutate(id)
    },
    onError: (err) => {
      setOpenRemarkDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to kiv application", "error");
    }
  })

  const rejectApplicationMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.rejectApplication}/${id}`, data)
    },
    onSuccess: () => {
      setOpenRemarkDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getApplication'] })
      openSnackbar("Successfully rejected application", "success");
      fetchApplicationMutation.mutate(id)
    },
    onError: (err) => {
      setOpenRemarkDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to reject application", "error");
    }
  })

  const resetApplicationMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.resetApplication}/${id}`, data)
    },
    onSuccess: () => {
      setOpenRemarkDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getApplication'] })
      openSnackbar("Successfully reset application", "success");
      fetchApplicationMutation.mutate(id)
    },
    onError: (err) => {
      setOpenRemarkDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to reset application", "error");
    }
  })

  const downloadFormMutation = useMutation({
    mutationFn: (data) => {
      // return axios.post(AdminApiEndpoints.createProductCategory, data)
      return true
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      openSnackbar("Successfully download form", "success");
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data?.message, "error");
      else openSnackbar("Failed to download form", "error");
    }
  })

  const onConfirmRemark = (data) => {
    if(remarkDialogAction === ACTION.KIV)
      kivApplicationMutation.mutate({kivremark: data.remark, processBy: user?.username, userGuid: user?.userGuid})
    else if(remarkDialogAction === ACTION.REJECT)
      rejectApplicationMutation.mutate({rejectRemark: data.remark, processBy: user?.username, userGuid: user?.userGuid})
    else if(remarkDialogAction === ACTION.RESET)
      resetApplicationMutation.mutate({resetRemark: data.remark, resetBy: user?.username, userGuid: user?.userGuid})
  }

  const onConfirmApprove = () => {
    approveApplicationMutation.mutate({processBy: user?.username, userGuid: user?.userGuid})
  }

  const onBack = () => {
    navigate('/admin/applications', { replace: true });
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (!fetchApplicationMutation.isPending && !fetchApplicationMutation.isError) && isCurrentModuleAdmin && 
          <div style={{display: 'flex', gap: "10px"}}>
          {data?.post?.paymentTerm === PAYMENT_TERM.POSTPAYMENT ?
            <>
            <CustomButton color="#4DB6AC" disabled={["Approved","Rejected"].includes(data?.applicationStatus?.valueEng)} onClick={()=>setOpenConfirmDialog(true)}>
              Approve
            </CustomButton>
            <CustomButton color="#BA68C8" disabled={["Approved","Rejected", "KIV"].includes(data?.applicationStatus?.valueEng)} onClick={()=>{setRemarkDialogAction(ACTION.KIV);setOpenRemarkDialog(true)}}>
              KIV
            </CustomButton>
            <CustomButton color="#D13F58" disabled={["Approved","Rejected"].includes(data?.applicationStatus?.valueEng)} onClick={()=>{setRemarkDialogAction(ACTION.REJECT);setOpenRemarkDialog(true)}}>
              Reject
            </CustomButton>
            <CustomButton color="#E9A035" disabled={["Pending","KIV"].includes(data?.applicationStatus?.valueEng) || data?.activityPaymentGuid} onClick={()=>{setRemarkDialogAction(ACTION.RESET);setOpenRemarkDialog(true)}}>
              Reset
            </CustomButton>
            </>
          : data?.post?.paymentTerm === PAYMENT_TERM.PREPAYMENT ? 
            <>
              <CustomButton color="#007BFF" disabled={!data?.activityPayment} component={RouterLink} to={`/admin/activity-payment/view?id=${data?.activityPaymentGuid}`} target="_blank" rel="noopener noreferrer">
                View Payment
              </CustomButton>
            </>
          : null
          }
          </div>
        }
      />
      <SkeletonWrapper isLoading={fetchApplicationMutation.isPending} isError={fetchApplicationMutation.isError} errorMessage="Unable to load application" disabled={["create"].includes(pageAction)}>
      <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Application Details</Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.applicationNo}
                label="Application No"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={formatDateTime(data?.createdOn)}
                label="Date & Time"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.applicationStatus?.valueEng}
                label="Apply Status"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.activityPayment?.paymentStatus?.valueEng || '-'}
                label="Payment Status"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.processBy || '-'}
                label="Process By"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={formatDate(data?.processDate)}
                label="Process Date"
                readOnly
              />
            </Grid2>

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Customer Details</Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.customer?.customerName}
                label="Full Name"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.customer?.age}
                label="Age"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.customer?.sex}
                label="Gender"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.customer?.emailAddress}
                label="Email Address"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.customer?.contactNumber}
                label="Contact Number"
                readOnly
              />
            </Grid2>

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Form Details</Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.post?.postName}
                label="Post Name"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.formDetails?.form?.formName}
                label="Form Name"
                readOnly
              />
            </Grid2>

            <Grid2 size={12}>
              <Divider />
            </Grid2>

            {data?.formAnswersData.map((item, index) => (
              <React.Fragment key={index}>
                <Grid2 size={12}>
                  <Typography fontSize={"0.7rem"} color="#555555">Data {item?.data}</Typography>
                  <Typography fontSize={"0.9rem"} color="#5D5D5D" fontWeight={600}>{item?.question}</Typography>
                </Grid2>
                <Grid2 size={12}>
                  {
                    item.answers.map((answer, index) => (
                      <React.Fragment key={index}>
                        <CustomTextField 
                          value={answer?.answer}
                          label="Answer"
                          readOnly
                        />
                        {answer.remark &&
                          <CustomTextField 
                            value={answer?.remark}
                            label="Sub Text"
                            readOnly
                            sx={{marginTop: '10px'}}
                          />
                        }
                      </React.Fragment>
                    ))
                  }
                  
                </Grid2>
              </React.Fragment>
            ))
            }

            <Grid2 size={12} display={'flex'} justifyContent={"flex-end"} gap={"10px"}>
              <CustomButton onClick={()=>setOpenFormDialog(true)}>
                View Form
              </CustomButton>
              <PDFDownloadLink document={<FormPDF data={data}/>} fileName={`${data?.applicationNo}.pdf`}>
                {({ loading }) => 
                  <CustomButton color="#6C757D" loading={downloadFormMutation.isPending || loading}>
                    Download Form
                  </CustomButton>
                }
              </PDFDownloadLink>
            </Grid2>

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Payment Details</Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.activityPayment?.paymentNo || '-'}
                label="Payment No"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.applicationAcceptanceNo?.acceptanceNumber || '-'}
                label="Acceptance No"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.postFeeOption?.optionNameEng}
                label="Option"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.postFeeOption?.fee}
                label="Option Fee (RM)"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={formatDate(data?.activityPayment?.paymentDate)}
                label="Pay Date"
                readOnly
              />
            </Grid2>

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Other Details</Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.kivremark || '-'}
                label="KIV Remark"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.rejectRemark || '-'}
                label="Reject Remark"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.resetBy || '-'}
                label="Reset By"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={formatDate(data?.resetDate)}
                label="Reset Date"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.resetFromStatus?.valueEng || '-'}
                label="Reset From"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.resetRemark || '-'}
                label="Reset Remark"
                readOnly
              />
            </Grid2>

          </Grid2>
        </Paper>

      </div>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Accept Application"
        dialogDescription="Are you sure you want to accept this application?"
        onSubmit={onConfirmApprove}
        loading={approveApplicationMutation.isPending}
      />
      <RemarkDialog 
        open={openRemarkDialog}
        onClose={()=>setOpenRemarkDialog(false)}
        dialogTitle={`${remarkDialogAction} Application`}
        onSubmit={onConfirmRemark}
        loading={
          kivApplicationMutation.isPending || rejectApplicationMutation.isPending || resetApplicationMutation.isPending
        }
      />
      <ViewFormDialog 
        open={openFormDialog}
        onClose={()=>setOpenFormDialog(false)}
        formAnswers={data?.formAnswers}
      />
    </div>
  );
}
